// ============================================================================
// BOOKING SOURCES (enum-driven)
// ============================================================================
// The selectable booking sources, read from common.tbl_enum_master
// (enum_group = 'BOOKING_SOURCE') rather than a hardcoded array.
//
// Every source dropdown in the app uses this hook. Before it existed the list
// was a 5-key literal in booking.utils.ts, which meant WEBSITE and GOVERNMENT
// were seeded in the enum but had never been selectable, and the dropdown could
// silently disagree with what the database accepted. Adding or retiring a source
// is now an enum-only change — no code edit, no deploy.
//
// `value` is the enum_value (the display label), because that is what
// booking.tbl_booking.booking_source stores — the CRS convention for name-based
// columns. See crs-doc/CRS-Booking-Source-Vocabulary-Fix-Design.md.
//
// Deliberately NO hardcoded fallback list: if the lookup fails the caller shows
// an error and an empty list. A stale fallback is worse than an empty one — it
// would offer values the server may reject, which is the exact bug being fixed.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { enumService } from 'api/services/enumService';
import { BOOKING_SOURCE_ENUM_GROUP } from 'components/modals/modules/booking-module/booking.utils';
import type { SelectOption } from 'components/ui/SyutSelect';

interface UseBookingSourcesResult {
  /** Ready for SyutSelect / native select, in the enum's own sort order. */
  options: SelectOption[];
  loading: boolean;
  /** Non-null when the lookup failed — callers should surface it, not paper over it. */
  error: string | null;
  reload: () => void;
}

export const useBookingSources = (): UseBookingSourcesResult => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    enumService
      .getByGroup(BOOKING_SOURCE_ENUM_GROUP)
      .then((items) => {
        if (cancelled) return;
        // enumService already returns active rows ordered by sort_order.
        setOptions(items.map((i) => ({ value: i.enumValue, label: i.enumValue })));
      })
      .catch(() => {
        if (cancelled) return;
        setOptions([]);
        setError('Could not load booking sources. Please retry.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [reloadKey]);

  return useMemo(
    () => ({ options, loading, error, reload }),
    [options, loading, error, reload],
  );
};

export default useBookingSources;
